import * as React from "react";
import styled from "@emotion/styled";
import type {
  FieldPathByValue,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import {
  Button,
  FieldError,
  FlexRow,
  Img,
  Label,
  LoadingSpinner,
  RequiredText,
  Stack,
  Text,
} from "atoms";
import { useImageUpload } from "../api/useUploadImage";
import type { Image, UploadedImage } from "../types";
import { FileInput, HiddenInput } from "./FileInput";
import { ImageCropper } from "./ImageCropper";
import { PreviewVideo } from "./PreviewVideo";
import { generateThumbnail } from "../utils/generateThumbnail";
import { formatSize } from "../utils/formatSize";

const ImageAspectRatioMap = {
  square: 1.0,
  banner: 3.0,
};

type ImageAspectRatio = keyof typeof ImageAspectRatioMap;

type ImageUploadProps<TFieldValues extends FieldValues, TFieldName> = {
  errorMessage?: string;
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  /**
   * Optional handler to use uploaded url or file imperatively
   */
  onSuccess?: (upload: UploadedImage) => void;
  fieldName: TFieldName;
  src?: Image;
  entityType?: "ARTIST" | "PROJECT";
  maxFileSize?: number;
  accept?: React.ComponentProps<typeof FileInput>["accept"];
  /**
   * Optional aspect ratio to require image cropping, undefined value prevents need to crop
   */
  aspect?: ImageAspectRatio | number;
  label?: React.ReactNode;
  required?: string | boolean;
  previewWidth?: React.CSSProperties["width"];
};

function ImageUpload<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPathByValue<TFieldValues, Image | undefined>
>({
  accept,
  aspect,
  entityType,
  errorMessage,
  fieldName,
  label,
  maxFileSize,
  onSuccess,
  previewWidth = "50%",
  register,
  required,
  setValue,
  src,
  ...props
}: ImageUploadProps<TFieldValues, TFieldName>) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const thumbnailRef = React.useRef<HTMLCanvasElement>(null);
  const timerRef = React.useRef<number>();

  const [file, setFile] = React.useState<File>();

  const [preview, setPreview] = React.useState(src);
  const resetPreviewToSrc = () => setPreview(src);
  const removePreview = () => {
    setPreview(undefined);
    if (file) setFile(undefined);
  };

  const { runUpload, upload, isLoading, error } = useImageUpload();
  const thumbnailMutation = useImageUpload();

  React.useEffect(() => {
    if (upload) {
      // We have a successful upload, convert it to Image shape for preview
      const blobURL = URL.createObjectURL(upload.file);
      const isVideo = upload.file.type.startsWith("video");
      setPreview({
        url: isVideo ? "" : blobURL,
        contentType: upload.file.type,
        importDate: "",
        videoUrl: isVideo ? blobURL : undefined,
      });
      return () => URL.revokeObjectURL(blobURL);
    }
    return () => {};
  }, [upload]);

  // Clean up any remaining timeout on unmount
  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const requiresCrop = aspect !== undefined;
  const fileIsImage = !!file && file.type.startsWith("image");
  const previewIsVideo = !!preview && !!preview.videoUrl;

  const handleGenerateThumb = async () => {
    const thumbFile = await generateThumbnail(
      thumbnailRef.current,
      videoRef.current
    );
    if (thumbFile) {
      thumbnailMutation.runUpload(thumbFile, {
        onSuccess: (result) => {
          setValue(
            `${fieldName}.url` as Path<TFieldValues>,
            result.url as PathValue<TFieldValues, Path<TFieldValues>>
          );
        },
      });
    }
  };

  // Upload selected and/or cropped file and show upload preview
  const uploadFile = (newFile: File) => {
    runUpload(newFile, {
      // When upload is successful, update the parent component (e.g. set form field values)
      onSuccess: (result) => {
        const isVideo = result.file.type.startsWith("video");
        setValue(
          fieldName,
          {
            url: isVideo ? "" : result.url,
            videoUrl: isVideo ? result.url : "",
            contentType: result.file.type,
            importDate: new Date().toISOString(),
          } as PathValue<TFieldValues, TFieldName>,
          {
            shouldDirty: true,
            shouldTouch: true,
          }
        );
        if (onSuccess) onSuccess(result);
      },
    });
  };

  const cancelCrop = () => {
    // Remove value from file input
    const element = inputRef.current;
    if (element) element.value = "";

    // Reset to preview if original src exists, otherwise show empty file input
    if (src) {
      resetPreviewToSrc();
    } else {
      removePreview();
    }
  };

  // useForm's `register()` options for hidden input
  const registerOpts = required
    ? { required: "This upload is required" }
    : undefined;

  let previewVariant: React.ComponentProps<typeof Img>["variant"] = "default";
  if (entityType === "ARTIST") previewVariant = "circle";
  if (entityType === "PROJECT") previewVariant = "rounded";

  // Once video has enough data to start playing, there should be a first frame showing. Make
  // doubly sure by adding a timeout. Then generate the first thumbnail of the preview video.
  const onCanPlay = () => {
    // Save timeout in case we need to clean it up on unmount
    timerRef.current = window.setTimeout(handleGenerateThumb, 500);
  };

  return (
    <div {...props}>
      {label && <FakeLabel>{label}</FakeLabel>}

      {preview ? (
        <>
          <UploadedPreview>
            <FlexRow gap="10px">
              {upload && (
                <SText size="xxs">
                  {upload.file.name} — {formatSize(upload.file.size)}
                </SText>
              )}

              <SText size="xxs" bold>
                <Button onClick={removePreview} size="xs" variant="text">
                  Replace {previewIsVideo ? "Video" : "Upload"}
                </Button>
              </SText>
            </FlexRow>

            <div>
              {previewIsVideo ? (
                <PreviewVideo
                  videoRef={videoRef}
                  src={preview.videoUrl ?? ""}
                  onCanPlay={onCanPlay}
                />
              ) : (
                <Img
                  src={preview.url}
                  alt="Upload preview"
                  variant={previewVariant}
                  style={{ width: previewWidth }}
                />
              )}
            </div>
          </UploadedPreview>

          {upload && previewIsVideo && (
            <ThumbnailUploadContainer>
              <FakeLabel>
                Thumbnail Image <RequiredText />
              </FakeLabel>

              <FlexRow itemsFlex="1 1 150px" alignItems="flex-start">
                <ThumbnailPreview ref={thumbnailRef} />

                <Stack>
                  <Button
                    variant="secondary"
                    size="xxs"
                    onClick={() => handleGenerateThumb()}
                  >
                    Generate Thumbnail
                  </Button>

                  <Text size="xxxs">
                    A thumbnail image has been created using the first frame of
                    your video. Click &ldquo;Generate Thumbnail&rdquo; when the
                    above video is on a new frame to use it as the thumbnail
                    image.
                  </Text>
                </Stack>
              </FlexRow>
            </ThumbnailUploadContainer>
          )}
        </>
      ) : (
        <>
          {!file && (
            <FileInput
              accept={accept}
              maxFileSize={maxFileSize}
              onCancel={src ? resetPreviewToSrc : undefined}
              onChange={requiresCrop ? setFile : uploadFile}
              ref={inputRef}
            />
          )}

          {fileIsImage && requiresCrop && (
            <ImageCropper
              aspect={
                typeof aspect === "string"
                  ? ImageAspectRatioMap[aspect]
                  : aspect
              }
              file={file}
              onCancel={cancelCrop}
              onSubmit={uploadFile}
            />
          )}
        </>
      )}

      {(isLoading || thumbnailMutation.isLoading) && (
        <LoadingSpinner text="Uploading" />
      )}

      <HiddenInput
        type="hidden"
        readOnly
        {...register(`${fieldName}.url` as Path<TFieldValues>, registerOpts)}
      />

      {(errorMessage || error) && (
        <FieldError>
          {errorMessage ?? "Sorry, the upload failed. Try again."}
        </FieldError>
      )}
    </div>
  );
}

const FakeLabel = styled(Label)({
  marginBottom: ".5rem",
}).withComponent("div");

const UploadedPreview = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "start",
  gap: "1rem",
  alignItems: "center",
});

const ThumbnailUploadContainer = styled.div({
  margin: "1rem 0 0",
});

const ThumbnailPreview = styled.canvas(({ theme }) => ({
  width: "50%",
  border: "1px solid",
  borderColor: theme.colors.fg30,
  borderRadius: 4,
}));

const SText = styled(Text)({
  marginBottom: 0,
});

export { ImageUpload, FakeLabel };
